import api from '@/api';
import { fileUploadRequest } from '@/api/request';
import { PageResult } from '@/core/request/PageResult';
import { API_BASE_URL } from '.';
import {
  CalculateTaxesPurchaseFreightInvoice,
  LastConsumablePurchasesRequest,
  LastItemPurchases,
  LastItemPurchasesRequest,
  Purchase,
  PurchaseAllXMLRequest,
  PurchaseAuthorizeRequest,
  PurchaseCreateRequest,
  PurchaseGroupCreate,
  PurchaseGrouped,
  PurchaseImportXmlRequest,
  PurchaseListParams,
  PurchaseListResponse,
  PurchaseUpdateRequest,
  PurchaseUploadXmlFileRequest,
  purchaseXmlInterface,
} from './Purchase.types';
import { PurchaseGroupingQuery } from './PurchaseGroup/PurchaseGroup.types';

import {
  PurchaseCancelRequest,
  PurchaseImportConfirmRequest,
  PurchaseImportConfirmResponse,
  PurchaseImportResponse,
} from './purchaseImport/PurchaseImport.types';
import { PurchaseImportCsvRequest } from '@/components/Movements/PurchasesPartsLink';
import { ACCESS_TOKEN } from '@/utils/user/constants';
import instance from '@/api/axiosInstance';

const CONTROLLER_URL = `${API_BASE_URL}/purchases`;

export async function purchasesList(params?: PurchaseListParams) {
  const result = (await api.get(`${CONTROLLER_URL}`, params)) as unknown;

  return result as PageResult<PurchaseListResponse>;
}

export async function getPurchase(purchaseId: number) {
  const result = (await api.get(
    `${CONTROLLER_URL}/${purchaseId}`,
    null,
  )) as unknown;

  return result as Purchase;
}

export async function getPurchaseXml(purchase: { purchaseId?: number }) {
  const result = (await api.get(
    `${CONTROLLER_URL}/billingXml`,
    purchase,
  )) as unknown;

  return result as purchaseXmlInterface[];
}

export async function purchaseCreate(
  obj: PurchaseCreateRequest,
): Promise<Purchase> {
  const result = (await api.post(`${CONTROLLER_URL}`, obj, null)) as unknown;

  return result as Purchase;
}

export async function purchaseUpdate(
  obj: PurchaseUpdateRequest,
): Promise<Purchase> {
  const result = (await api.put(`${CONTROLLER_URL}`, obj, null)) as unknown;

  return result as Purchase;
}

export async function purchaseRemove(id: number) {
  const result = (await api.remove(
    `${CONTROLLER_URL}/${id}`,
    {},
    null,
  )) as unknown;

  return result;
}

export async function getLastConsumablesPurchases(
  params?: LastConsumablePurchasesRequest,
) {
  const result = (await api.get(
    `${CONTROLLER_URL}/consumableId/${params?.consumableId}`,
    {
      orderByColumn: params?.orderByColumn,
    },
  )) as unknown;

  return result as LastItemPurchases[];
}

export async function calculateTaxesPurchase(purchaseId: number) {
  const result = (await api.put(
    `${CONTROLLER_URL}/calc-taxes/${purchaseId}`,
    {},
    null,
  )) as unknown;

  return result as Purchase;
}

export async function calculateTaxesPurchaseFreightInvoice(
  obj: CalculateTaxesPurchaseFreightInvoice,
) {
  const { purchaseId, ...values } = obj;
  const result = (await api.put(
    `${CONTROLLER_URL}/freight-invoice/calc-taxes/${purchaseId}`,
    values,
    null,
  )) as unknown;

  return result as Purchase;
}

export async function getLastPartsPurchases(params: LastItemPurchasesRequest) {
  const result = (await api.get(`${CONTROLLER_URL}/partId/${params?.partId}`, {
    orderByColumn: params.orderByColumn,
  })) as unknown;

  return result as LastItemPurchases[];
}

export async function listPurchasesGroup(
  params: PurchaseGroupingQuery,
): Promise<PageResult<Purchase>> {
  const result = (await api.get(`${CONTROLLER_URL}/group`, params)) as unknown;

  return result as PageResult<Purchase>;
}

export async function purchaseGroupCreate(
  obj: PurchaseGroupCreate,
): Promise<Purchase> {
  const result = (await api.post(
    `${CONTROLLER_URL}/group?documentTypeId=${obj.documentTypeId}`,
    obj.body,
    null,
  )) as unknown;

  return result as Purchase;
}

export async function purchaseAuthorize(
  obj: PurchaseAuthorizeRequest,
): Promise<Purchase> {
  const result = (await api.put(
    `${CONTROLLER_URL}/authorize?changeRegisteredData=${obj.change}&purchaseId=${obj.purchaseId}`,
    {},
    null,
  )) as unknown;

  return result as Purchase;
}

export async function getImportPurchases(obj: PurchaseImportXmlRequest) {
  const file = new Blob([obj.file], {
    type: 'application/xml',
  });

  const result = (await fileUploadRequest(
    `${CONTROLLER_URL}/import`,
    [],
    {},
    file,
  )) as unknown;

  return result as PurchaseImportResponse;
}

export async function purchaseConfirmImport(
  obj: PurchaseImportConfirmRequest,
  file?: File,
) {
  const accessToken = localStorage.getItem(ACCESS_TOKEN);
  const formData = new FormData();

  formData.append('file', file ?? '');
  formData.append('purchaseRequest', JSON.stringify(obj));

  const result = (await instance.post(
    `${CONTROLLER_URL}/import/confirm`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
        Authorization: `Bearer ${accessToken}`,
      },
    },
  )) as unknown;

  return result as PurchaseImportConfirmResponse;
}

export async function purchaseCancel(obj: PurchaseCancelRequest) {
  const { cancellationReason, purchaseId } = obj;

  const result = (await api.put(
    `${CONTROLLER_URL}/cancel/${purchaseId}`,
    { cancellationReason },
    null,
  )) as unknown;

  return result;
}

export async function getPurchaseGrouped(purchaseId: number) {
  const result = (await api.get(
    `${CONTROLLER_URL}/group/grouped-purchases/purchase/${purchaseId}`,
    null,
  )) as unknown;

  return result as PurchaseGrouped[];
}

// PartsLink

export async function getImportPurchasesCsv(obj: PurchaseImportCsvRequest) {
  const file = new Blob([obj.file], {
    type: 'application/csv',
  });
  const result = (await fileUploadRequest(
    `${CONTROLLER_URL}/import/partslink24`,
    [],
    {},
    file,
  )) as unknown;
  return result as Purchase;
}

export async function purchaseSendBoschXml(purchaseId: number) {
  const result = (await api.post(
    `${CONTROLLER_URL}/${purchaseId}/send-bosch-xml`,
    {},
  )) as unknown;

  return result as void;
}

export async function purchaseDownloadXml(purchaseId: number) {
  const result = (await api.getReport(
    `${CONTROLLER_URL}/${purchaseId}/download-xml`,
    {},
  )) as unknown;

  return result as BlobPart;
}

export async function purchaseDownloadAllXml(params: PurchaseAllXMLRequest) {
  const result = (await api.getReport(
    `${CONTROLLER_URL}/all-xml`,
    params,
  )) as unknown;
  return result as BlobPart;
}

export async function purchaseUploadXmlFile(
  params: PurchaseUploadXmlFileRequest,
) {
  const file = new Blob([params.file], {
    type: 'application/xml',
  });

  const result = (await fileUploadRequest(
    `${CONTROLLER_URL}/uploadXmlFile/${params.purchaseId}`,
    [],
    {},
    file,
  )) as unknown;

  return result as void;
}
